import { useState, useCallback, useEffect, useMemo } from 'react'
import { prodexer } from '../api'
import type { Transfer, Reward, ReputerStake, Transaction, ValidatorDelegation, FundTopic, Network } from '../types'
import {
  useQuery,
  SearchInput,
  Spinner,
  ErrorBanner,
  SectionHeader,
  StatCard,
  DataTable,
  LinkToCopy,
  walletUrl,
  blockUrl,
  topicUrl,
  AmountDisplay,
  formatAmount,
  formatNumber,
  truncate,
  formatTime,
} from '../components'
import { TimeRangeSelector, timeRangeToBlockHeights } from '../TimeRange'
import type { TimeRange } from '../TimeRange'
import { useFavorites } from '../useFavorites'
import { GroupedTransferTable } from '../TransferExpansion'

export function WalletExplorer({ network, initialQuery }: { network: Network; initialQuery?: string }) {
  const transfersQ = useQuery<Transfer>()
  const receivedTransfersQ = useQuery<Transfer>()
  const rewardsQ = useQuery<Reward>()
  const stakesQ = useQuery<ReputerStake>()
  const txsQ = useQuery<Transaction>()
  const delegationsQ = useQuery<ValidatorDelegation>()
  const fundTopicQ = useQuery<FundTopic>()

  const [address, setAddress] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [activeTab, setActiveTab] = useState<'summary' | 'transfers' | 'received' | 'rewards' | 'stakes' | 'txs' | 'delegations' | 'funding'>('summary')
  const [timeRange, setTimeRange] = useState<TimeRange>({ preset: '90d' })
  const [latestHeight, setLatestHeight] = useState<number | null>(null)
  const { favorites, toggle, updateLabel, isFavorite } = useFavorites(network)

  // Fetch latest block height when network changes
  useEffect(() => {
    prodexer.fetchLatestHeight().then(setLatestHeight)
  }, [network])

  const executeSearch = useCallback(async (addr: string, range: TimeRange, height: number | null) => {
    setAddress(addr)
    setHasSearched(true)

    // Resolve time range → block height bounds (skip if height unknown)
    const heightRange = height ? timeRangeToBlockHeights(range, height) : {}
    const startHeight = heightRange.start
    const heightFilters = startHeight
      ? [{ column: 'block_height', op: 'gte' as const, value: String(startHeight) }]
      : []

    await Promise.all([
      // transfers (sent): idx_transfers_sender_block_height (sender, block_height)
      transfersQ.execute(() =>
        prodexer.query<Transfer>('transfers', {
          filters: [
            { column: 'sender', op: 'eq', value: addr },
            ...heightFilters,
          ],
          order: [{ column: 'block_height', desc: true }],
          limit: 1000,
        })
      ),
      // transfers (received): idx_transfers_recipient_block_height (recipient, block_height)
      receivedTransfersQ.execute(() =>
        prodexer.query<Transfer>('transfers', {
          filters: [
            { column: 'recipient', op: 'eq', value: addr },
            ...heightFilters,
          ],
          order: [{ column: 'block_height', desc: true }],
          limit: 1000,
        })
      ),
      // rewards: client-side filter (can't add block_height to index filter)
      rewardsQ.execute(() =>
        prodexer.query<Reward>('rewards', {
          filters: [{ column: 'address', op: 'eq', value: addr }],
          limit: 1000,
        }).then(rows =>
          startHeight ? rows.filter(r => r.block_height >= startHeight) : rows
        )
      ),
      // reputer_stakes: query by delegator, client-side filter
      stakesQ.execute(() =>
        prodexer.query<ReputerStake>('reputer_stakes', {
          filters: [{ column: 'delegator', op: 'eq', value: addr }],
          limit: 1000,
        }).then(rows =>
          startHeight ? rows.filter(s => s.block_height >= startHeight) : rows
        )
      ),
      // transactions: idx_transactions_sender_block_height (sender_address, block_height)
      txsQ.execute(() =>
        prodexer.query<Transaction>('transactions', {
          filters: [
            { column: 'sender_address', op: 'eq', value: addr },
            ...heightFilters,
          ],
          order: [{ column: 'block_height', desc: true }],
          limit: 1000,
        })
      ),
      // validator_delegations: client-side filter
      delegationsQ.execute(() =>
        prodexer.query<ValidatorDelegation>('validator_delegations', {
          filters: [{ column: 'delegator', op: 'eq', value: addr }],
          limit: 1000,
        }).then(rows =>
          startHeight ? rows.filter(d => d.block_height >= startHeight) : rows
        )
      ),
      // fund_topic: client-side filter
      fundTopicQ.execute(() =>
        prodexer.query<FundTopic>('fund_topic', {
          filters: [{ column: 'funder', op: 'eq', value: addr }],
          limit: 1000,
        }).then(rows =>
          startHeight ? rows.filter(f => f.block_height >= startHeight) : rows
        )
      ),
    ])
  }, [transfersQ, receivedTransfersQ, rewardsQ, stakesQ, txsQ, delegationsQ, fundTopicQ])

  // Initial search
  const search = useCallback((addr: string) => {
    executeSearch(addr, timeRange, latestHeight)
  }, [executeSearch, timeRange, latestHeight])

  // Auto-search on initialQuery (URL deep-link).
  // Wait for latestHeight to resolve so the 90d time range filter is applied
  // on the first search, not skipped (cubic P2).
  useEffect(() => {
    if (initialQuery && latestHeight !== null) {
      search(initialQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestHeight])

  // Reactive: re-query when timeRange changes (only if already searched)
  useEffect(() => {
    if (hasSearched && address) {
      executeSearch(address, timeRange, latestHeight)
    }
  }, [timeRange])  // intentionally not depending on address/hasSearched to avoid re-trigger

  // --- Compute summary stats (null-safe + memoized) ---
  const summary = useMemo(() => {
    const sentTransfers = transfersQ.data ?? []
    const totalSent = sentTransfers.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0)
    const sentByRecipient = new Map<string, number>()
    for (const t of sentTransfers) {
      const v = parseFloat(t.amount) || 0
      sentByRecipient.set(t.recipient, (sentByRecipient.get(t.recipient) ?? 0) + v)
    }

    const receivedTransfers = receivedTransfersQ.data ?? []
    const totalReceived = receivedTransfers.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0)
    const receivedBySender = new Map<string, number>()
    for (const t of receivedTransfers) {
      const v = parseFloat(t.amount) || 0
      receivedBySender.set(t.sender, (receivedBySender.get(t.sender) ?? 0) + v)
    }

    const rewards = rewardsQ.data ?? []
    const totalRewards = rewards.reduce((s, r) => s + (parseFloat(r.reward) || 0), 0)
    const rewardsByTopic = new Map<number, number>()
    const rewardsByActor = new Map<string, number>()
    for (const r of rewards) {
      const v = parseFloat(r.reward) || 0
      rewardsByTopic.set(r.topic_id, (rewardsByTopic.get(r.topic_id) ?? 0) + v)
      rewardsByActor.set(r.actor_type, (rewardsByActor.get(r.actor_type) ?? 0) + v)
    }

    const stakes = stakesQ.data ?? []
    const totalStaked = stakes
      .filter((s) => s.request_type === 'ADD_STAKE')
      .reduce((s, st) => s + (parseFloat(st.amount) || 0), 0)
    const totalUnstaked = stakes
      .filter((s) => s.request_type === 'REMOVE_STAKE')
      .reduce((s, st) => s + (parseFloat(st.amount) || 0), 0)

    const funding = fundTopicQ.data ?? []
    const totalFunded = funding.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0)

    return {
      sentTransfers, totalSent, sentByRecipient,
      receivedTransfers, totalReceived, receivedBySender,
      netTransfer: totalReceived - totalSent,
      rewards, totalRewards, rewardsByTopic, rewardsByActor,
      stakes, totalStaked, totalUnstaked,
      txs: txsQ.data ?? [],
      delegations: delegationsQ.data ?? [],
      funding, totalFunded,
    }
  }, [transfersQ.data, receivedTransfersQ.data, rewardsQ.data, stakesQ.data, txsQ.data, delegationsQ.data, fundTopicQ.data])

  const {
    sentTransfers, totalSent, sentByRecipient,
    receivedTransfers, totalReceived, receivedBySender,
    netTransfer,
    rewards, totalRewards, rewardsByTopic, rewardsByActor,
    stakes, totalStaked, totalUnstaked,
    txs, delegations, funding, totalFunded,
  } = summary

  const loading = transfersQ.loading || receivedTransfersQ.loading || rewardsQ.loading || stakesQ.loading || txsQ.loading || delegationsQ.loading || fundTopicQ.loading
  const error = transfersQ.error || receivedTransfersQ.error || rewardsQ.error || stakesQ.error || txsQ.error || delegationsQ.error || fundTopicQ.error

  const tabs = [
    { id: 'summary' as const, label: 'Summary', count: null },
    { id: 'transfers' as const, label: 'Sent', count: sentTransfers.length },
    { id: 'received' as const, label: 'Received', count: receivedTransfers.length },
    { id: 'txs' as const, label: 'Transactions', count: txs.length },
    { id: 'rewards' as const, label: 'Rewards', count: rewards.length },
    { id: 'stakes' as const, label: 'Stakes', count: stakes.length },
    { id: 'delegations' as const, label: 'Delegations', count: delegations.length },
    { id: 'funding' as const, label: 'Funding', count: funding.length },
  ]

  return (
    <div>
      <SectionHeader
        title="Wallet Explorer"
        subtitle="Aggregate on-chain activity for an Allora address across transfers, rewards, stakes, and transactions."
      />

      <SearchInput
        placeholder="allo1... (sender/recipient/reputer/delegator/funder)"
        onSubmit={search}
        loading={loading}
        buttonText="Query Wallet"
        favorites={favorites}
        onToggleFavorite={(addr, label) => toggle(addr, label)}
        onUpdateLabel={updateLabel}
        isFavorite={address ? isFavorite(address) : false}
        currentValue={address}
      />

      <div className="mt-3 flex items-center justify-between gap-4">
        <div className="text-xs text-text-muted">
          {hasSearched && address && (
            <>
              Querying as: <span className="font-mono text-accent-text">{address}</span>
              <span className="ml-2">({prodexer.networkLabel})</span>
            </>
          )}
        </div>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {error && !loading && (
        <div className="mt-4">
          <ErrorBanner error={error} />
        </div>
      )}

      {!loading && hasSearched && !error && (
        <>
          {/* Tab bar */}
          <div className="mt-6 flex flex-wrap gap-1 border-b border-border-default">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-accent text-accent-text'
                    : 'border-transparent text-text-muted hover:text-text-secondary'
                }`}
              >
                {tab.label}
                {tab.count !== null && tab.count > 0 && (
                  <span className="ml-1.5 rounded-full bg-bg-tertiary px-1.5 py-0.5 text-xs text-text-muted">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="mt-4">
            {activeTab === 'summary' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <StatCard
                    label="Total Sent"
                    value={formatAmount(totalSent)}
                    sublabel={`${sentTransfers.length} transfers`}
                    accent="red"
                  />
                  <StatCard
                    label="Total Received"
                    value={formatAmount(totalReceived)}
                    sublabel={`${receivedTransfers.length} transfers`}
                    accent="green"
                  />
                  <StatCard
                    label="Net Transfer"
                    value={formatAmount(netTransfer)}
                    sublabel={netTransfer >= 0 ? 'net inflow' : 'net outflow'}
                    accent={netTransfer >= 0 ? 'green' : 'red'}
                  />
                  <StatCard
                    label="Total Rewards"
                    value={formatAmount(totalRewards)}
                    sublabel={`${rewards.length} reward events`}
                    accent="green"
                  />
                  <StatCard
                    label="Net Staked"
                    value={formatAmount(totalStaked - totalUnstaked)}
                    sublabel={`${stakes.length} stake events`}
                    accent="blue"
                  />
                  <StatCard
                    label="Transactions"
                    value={formatNumber(txs.length)}
                    sublabel="as sender"
                  />
                  <StatCard
                    label="Delegations"
                    value={formatNumber(delegations.length)}
                    sublabel="validator delegations"
                  />
                  <StatCard
                    label="Topic Funding"
                    value={formatAmount(totalFunded)}
                    sublabel={`${funding.length} fund events`}
                    accent="amber"
                  />
                </div>

                {/* Top Recipients breakdown (outgoing) */}
                {sentByRecipient.size > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-text-secondary mb-2">
                      Top Recipients (Outgoing)
                    </h3>
                    <div className="space-y-1.5">
                      {Array.from(sentByRecipient.entries())
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([recipient, amount]) => (
                          <div
                            key={recipient}
                            className="flex items-center justify-between rounded border border-border-default bg-surface px-3 py-1.5"
                          >
                            <LinkToCopy text={recipient} href={walletUrl(recipient)} display={<span className="text-sm font-mono text-success-text">{truncate(recipient)}</span>} className="hover:text-success-text" />
                            <AmountDisplay value={amount} className="text-sm font-mono text-amber-text" />
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Top Senders breakdown */}
                {receivedBySender.size > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-text-secondary mb-2">
                      Top Senders (Incoming)
                    </h3>
                    <div className="space-y-1.5">
                      {Array.from(receivedBySender.entries())
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([sender, amount]) => (
                          <div
                            key={sender}
                            className="flex items-center justify-between rounded border border-border-default bg-surface px-3 py-1.5"
                          >
                            <LinkToCopy text={sender} href={walletUrl(sender)} display={<span className="text-sm font-mono text-accent-text">{truncate(sender)}</span>} className="hover:text-accent-text" />
                            <span className="text-sm font-mono text-success-text">
                              {formatAmount(amount)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {rewardsByTopic.size > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-text-secondary mb-2">
                      Rewards by Topic
                    </h3>
                    <div className="space-y-1.5">
                      {Array.from(rewardsByTopic.entries())
                        .sort((a, b) => b[1] - a[1])
                        .map(([topicId, amount]) => (
                          <div
                            key={topicId}
                            className="flex items-center justify-between rounded border border-border-default bg-surface px-3 py-1.5"
                          >
                            <span className="text-sm font-mono text-text-muted">
                              Topic {topicId}
                            </span>
                            <AmountDisplay value={amount} className="text-sm font-mono text-amber-text" />
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {rewardsByActor.size > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-text-secondary mb-2">
                      Rewards by Actor Type
                    </h3>
                    <div className="space-y-1.5">
                      {Array.from(rewardsByActor.entries())
                        .sort((a, b) => b[1] - a[1])
                        .map(([actorType, amount]) => (
                          <div
                            key={actorType}
                            className="flex items-center justify-between rounded border border-border-default bg-surface px-3 py-1.5"
                          >
                            <span className="text-sm font-mono text-text-muted truncate">
                              {actorType}
                            </span>
                            <AmountDisplay value={amount} className="text-sm font-mono text-amber-text ml-4" />
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transfers' && (
              <GroupedTransferTable
                transfers={sentTransfers}
                direction="sent"
                emptyMessage="No outgoing transfers found for this address"
              />
            )}

            {activeTab === 'received' && (
              <GroupedTransferTable
                transfers={receivedTransfers}
                direction="received"
                emptyMessage="No incoming transfers found for this address"
              />
            )}

            {activeTab === 'rewards' && (
              <DataTable
                rows={rewards}
                emptyMessage="No rewards found for this address"
                columns={[
                  { key: 'block_height', label: 'Block', render: (r) => <LinkToCopy text={String(r.block_height)} href={blockUrl(r.block_height)} display={<span>{formatNumber(r.block_height)}</span>} /> },
                  { key: 'topic_id', label: 'Topic', render: (r) => <LinkToCopy text={String(r.topic_id)} href={topicUrl(r.topic_id)} display={<span>{r.topic_id}</span>} /> },
                  {
                    key: 'actor_type',
                    label: 'Actor Type',
                    render: (r) => (
                      <span className="text-text-muted">{r.actor_type.replace('ACTOR_TYPE_', '').replace('_UNSPECIFIED', '').toLowerCase()}</span>
                    ),
                  },
                  {
                    key: 'reward',
                    label: 'Reward (ALLO)',
                    render: (r) => (
                      <AmountDisplay value={r.reward} className="text-success-text" />
                    ),
                  },
                  {
                    key: 'created_at',
                    label: 'Time',
                    render: (r) => formatTime(r.created_at),
                  },
                ]}
              />
            )}

            {activeTab === 'stakes' && (
              <DataTable
                rows={stakes}
                emptyMessage="No reputer stake events for this address"
                columns={[
                  { key: 'block_height', label: 'Block', render: (r) => <LinkToCopy text={String(r.block_height)} href={blockUrl(r.block_height)} display={<span>{formatNumber(r.block_height)}</span>} /> },
                  { key: 'topic_id', label: 'Topic', render: (r) => <LinkToCopy text={String(r.topic_id)} href={topicUrl(r.topic_id)} display={<span>{r.topic_id}</span>} /> },
                  {
                    key: 'request_type',
                    label: 'Type',
                    render: (r) => (
                      <span className={r.request_type === 'ADD_STAKE' ? 'text-success-text' : 'text-danger-text'}>
                        {r.request_type}
                      </span>
                    ),
                  },
                  {
                    key: 'amount',
                    label: 'Amount (ALLO)',
                    render: (r) => <AmountDisplay value={r.amount} className="text-amber-text" />,
                  },
                  {
                    key: 'total_stake',
                    label: 'Total Stake',
                    render: (r) => <AmountDisplay value={r.total_stake} />,
                  },
                  { key: 'delegator', label: 'Delegator', render: (r) => <LinkToCopy text={r.delegator} href={walletUrl(r.delegator)} display={<span className="text-accent-text">{truncate(r.delegator)}</span>} className="hover:text-accent-text" /> },
                  {
                    key: 'created_at',
                    label: 'Time',
                    render: (r) => formatTime(r.created_at),
                  },
                ]}
              />
            )}

            {activeTab === 'txs' && (
              <DataTable
                rows={txs}
                emptyMessage="No transactions found for this sender"
                columns={[
                  { key: 'block_height', label: 'Block', render: (r) => <LinkToCopy text={String(r.block_height)} href={blockUrl(r.block_height)} display={<span>{formatNumber(r.block_height)}</span>} /> },
                  {
                    key: 'message_types',
                    label: 'Message Types',
                    render: (r) => (
                      <div className="flex flex-wrap gap-1">
                        {r.message_types.map((mt, i) => (
                          <span
                            key={i}
                            className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs text-accent-text"
                          >
                            {mt.split('.').pop()}
                          </span>
                        ))}
                      </div>
                    ),
                  },
                  {
                    key: 'status_code',
                    label: 'Status',
                    render: (r) => (
                      <span className={r.status_code === 0 ? 'text-success-text' : 'text-danger-text'}>
                        {r.status_code === 0 ? '✓' : `✗ ${r.status_code}`}
                      </span>
                    ),
                  },
                  {
                    key: 'gas_used',
                    label: 'Gas Used',
                    render: (r) => formatNumber(r.gas_used),
                  },
                  {
                    key: 'created_at',
                    label: 'Time',
                    render: (r) => formatTime(r.created_at),
                  },
                ]}
              />
            )}

            {activeTab === 'delegations' && (
              <DataTable
                rows={delegations}
                emptyMessage="No validator delegations for this address"
                columns={[
                  { key: 'block_height', label: 'Block', render: (r) => <LinkToCopy text={String(r.block_height)} href={blockUrl(r.block_height)} display={<span>{formatNumber(r.block_height)}</span>} /> },
                  {
                    key: 'event_type',
                    label: 'Event',
                    render: (r) => (
                      <span className="text-text-muted">{r.event_type.replace(/_/g, ' ').toLowerCase()}</span>
                    ),
                  },
                  { key: 'delegator', label: 'Delegator', render: (r) => <LinkToCopy text={r.delegator} href={walletUrl(r.delegator)} display={<span className="text-accent-text">{truncate(r.delegator)}</span>} className="hover:text-accent-text" /> },
                  { key: 'validator', label: 'Validator', render: (r) => <LinkToCopy text={r.validator} href={walletUrl(r.validator)} display={<span className="text-accent-text">{truncate(r.validator)}</span>} className="hover:text-accent-text" /> },
                  { key: 'source_validator', label: 'Source', render: (r) => <LinkToCopy text={r.source_validator} href={walletUrl(r.source_validator)} display={<span className="text-accent-text">{truncate(r.source_validator)}</span>} className="hover:text-accent-text" /> },
                  { key: 'destination_validator', label: 'Destination', render: (r) => <LinkToCopy text={r.destination_validator} href={walletUrl(r.destination_validator)} display={<span className="text-accent-text">{truncate(r.destination_validator)}</span>} className="hover:text-accent-text" /> },
                  {
                    key: 'amount',
                    label: 'Amount (ALLO)',
                    render: (r) => formatAmount(r.amount),
                  },
                  {
                    key: 'created_at',
                    label: 'Time',
                    render: (r) => formatTime(r.created_at),
                  },
                ]}
              />
            )}

            {activeTab === 'funding' && (
              <DataTable
                rows={funding}
                emptyMessage="No topic funding events for this address"
                columns={[
                  { key: 'block_height', label: 'Block', render: (r) => <LinkToCopy text={String(r.block_height)} href={blockUrl(r.block_height)} display={<span>{formatNumber(r.block_height)}</span>} /> },
                  { key: 'topic_id', label: 'Topic', render: (r) => <LinkToCopy text={String(r.topic_id)} href={topicUrl(r.topic_id)} display={<span>{r.topic_id}</span>} /> },
                  {
                    key: 'amount',
                    label: 'Amount (ALLO)',
                    render: (r) => (
                      <AmountDisplay value={r.amount} className="text-amber-text" />
                    ),
                  },
                  {
                    key: 'created_at',
                    label: 'Time',
                    render: (r) => formatTime(r.created_at),
                  },
                ]}
              />
            )}
          </div>
        </>
      )}

      {!hasSearched && (
        <div className="mt-8 text-center text-sm text-text-faint">
          Enter an Allora address above to aggregate its on-chain activity.
          <br />
          <span className="text-xs">
            Queries sent/received transfers, rewards, reputer stakes, transactions,
            validator delegations, and topic funding — all filtered by the address.
          </span>
        </div>
      )}
    </div>
  )
}
